import { BigNumber, toBigNumber } from "@yjsgames/bignumber";
import { MODULE_BRICK_BALANCE } from "../data/balance.js";

const ZERO = BigNumber.ZERO;
const clampNonNeg = (v: BigNumber): BigNumber => (v.isNegative() ? ZERO : v);

/**
 * 경제 시스템 — 순수 로직 (Phaser 비의존, 단위 테스트 대상).
 * 골드/다이아 증감 + 상자/강화 비용 공식. 잔액 부족 시 trySpend*가 false를 반환하고 차감하지 않는다.
 * 골드 = 골드 상자/그리드 해금, 다이아 = 강화 상자 (지시문 §5·§12).
 *
 * 골드·다이아는 무한 성장하므로 BigNumber. 강화석(stone)은 소량 카운트라 number 유지(upgradeStoneCost).
 */
export class EconomyController {
  private _gold: BigNumber;
  private _diamond: BigNumber;

  constructor(
    initialGold: number | string | BigNumber = 0,
    initialDiamond: number | string | BigNumber = 0,
  ) {
    this._gold = clampNonNeg(toBigNumber(initialGold));
    this._diamond = clampNonNeg(toBigNumber(initialDiamond));
  }

  get gold(): BigNumber {
    return this._gold;
  }

  get diamond(): BigNumber {
    return this._diamond;
  }

  addGold(amount: BigNumber): void {
    if (amount.lte(ZERO)) return;
    this._gold = this._gold.add(amount);
  }

  /** 골드를 비용만큼 차감 시도. 충분하면 차감 + true, 부족하면 무변경 + false. */
  trySpend(cost: BigNumber): boolean {
    if (this._gold.lt(cost)) return false;
    this._gold = clampNonNeg(this._gold.sub(cost));
    return true;
  }

  /** 치트/환생용 — 골드 직접 설정. */
  setGold(amount: BigNumber): void {
    this._gold = clampNonNeg(amount);
  }

  addDiamond(amount: BigNumber): void {
    if (amount.lte(ZERO)) return;
    this._diamond = this._diamond.add(amount);
  }

  /** 다이아를 비용만큼 차감 시도. 충분하면 차감 + true, 부족하면 무변경 + false. */
  trySpendDiamond(cost: BigNumber): boolean {
    if (this._diamond.lt(cost)) return false;
    this._diamond = clampNonNeg(this._diamond.sub(cost));
    return true;
  }

  /** 치트/보상용 — 다이아 직접 설정. */
  setDiamond(amount: BigNumber): void {
    this._diamond = clampNonNeg(amount);
  }

  /** 골드 모듈 상자 가격: base + openCount * increase (지시문 §16·§19). */
  static goldBoxCost(boxOpenCount: number): BigNumber {
    return BigNumber.fromNumber(
      MODULE_BRICK_BALANCE.goldBoxBaseCost +
        Math.max(0, Math.floor(boxOpenCount)) * MODULE_BRICK_BALANCE.goldBoxCostIncrease,
    );
  }

  /**
   * 골드 상자 10연 가격 = 현재 단가 × goldBox10PayUnits (기본 9 = 10× 단가에서 1개분 할인).
   * 단가는 구매 시점 boxOpenCount 스냅샷(누진은 개봉 후 +10되어 다음 구매에 반영).
   */
  static goldBox10Cost(boxOpenCount: number): BigNumber {
    return EconomyController.goldBoxCost(boxOpenCount).mul(
      BigNumber.fromNumber(MODULE_BRICK_BALANCE.goldBox10PayUnits),
    );
  }

  /** 다이아 강화 상자 가격 (고정, 지시문 §12·§19). */
  static diamondBoxCost(): BigNumber {
    return BigNumber.fromNumber(MODULE_BRICK_BALANCE.diamondBoxCost);
  }

  /** 다음 환생 요구 챕터 초기화 상품 가격 (다이아, shop.tsv). */
  static rebirthConditionResetCost(): BigNumber {
    return BigNumber.fromNumber(MODULE_BRICK_BALANCE.rebirthConditionResetDiamondCost);
  }

  /**
   * 모듈 레벨업 1회 강화석 비용 = base × growth^level (강화석 전용, 지수 누진). 최소 1.
   * 레벨은 moduleId별 플레이어 자산(PlayerModuleLevelController)이라 비용은 그 현재 레벨을 입력받는다.
   * 골드 비용은 폐기 — 강화석만 소모(다이아 직접 레벨업은 밸런스 확인 전까지 금지).
   */
  static upgradeStoneCost(level: number): number {
    const lv = Math.max(1, Math.floor(level));
    return Math.max(
      1,
      Math.floor(
        MODULE_BRICK_BALANCE.upgradeStoneBase *
          Math.pow(MODULE_BRICK_BALANCE.upgradeStoneCostGrowth, lv),
      ),
    );
  }
}
